<script lang="ts" setup>
	import usePurchaseInfo from '~/composables/usePurchaseInfo'
	import type { ListContext } from '~/components/Shared/list-item.vue'

	const props = defineProps<{
		planDetails: PlanInformationContext | null
	}>()
	const { currencyToNaira } = useCurrency()
	const { purchaseDate, renewalDate, canAutoDebit, premium, supportLine } = usePurchaseInfo({
		planInformation: computed(() => props.planDetails)
	})

	const details = computed<ListContext>(() => ({
		'Payment method': {
			value: 'paystack',
			wrapperClass: 'flex justify-between'
		},
		'Payment date': {
			value: purchaseDate.value.formatted,
			wrapperClass: 'flex justify-between'
		},
		'Renewal Date': {
			value: renewalDate.value.formatted,
			wrapperClass: 'flex justify-between border-t border-gray-200 pt-5 mt-3'
		},
		Amount: {
			value: currencyToNaira(premium.value || 0),
			pClass: 'text-primary-2 font-semibold text-sm',
			wrapperClass: 'flex justify-between'
		}
	}))

	const showTurnOffModal = ref(false)

	const handleToggleIntent = () => {
		if (canAutoDebit.value) showTurnOffModal.value = true
	}

	const contactSupport = () => {
		if (supportLine.value.link) window.location.href = supportLine.value.link
		showTurnOffModal.value = false
	}
</script>

<template>
	<div class="relative flex min-h-[416px] flex-col rounded border border-gray-200">
		<div class="flex w-full items-center justify-between bg-gray-100 p-3 sm:p-5">
			<h4 class="font-medium capitalize text-gray-600"> Amount Paid </h4>

			<span class="text-lg font-semibold text-blue-950" aria-labelledby="balance">
				{{ currencyToNaira(premium || 0) }}
			</span>
		</div>

		<shared-list-item
			:lists="details"
			ul-class="px-5 py-5 w-full"
			h-class="text-sm text-gray-600 font-medium tracking-wide"
			li-class="!py-3 border-b-0"
		>
			<template #default="{ value, ke }">
				<lazy-tools-icon v-if="ke === 'Payment method'" dir="other" icon="paystack-2" />
				<span
					v-else
					:class="
						cn(
							'text-sm font-medium tracking-wide text-gray-600',
							typeof value === 'object' && value?.pClass
						)
					"
					v-html="typeof value === 'object' ? value?.value : value"
				/>
			</template>
		</shared-list-item>

		<div class="relative z-10 mt-auto w-full bg-purple-50 px-5 py-12">
			<div class="flex items-center justify-center gap-x-3">
				<span class="text-base font-medium text-gray-700">Auto Renewal</span>
				<div class="cursor-pointer" role="button" @click="handleToggleIntent">
					<shared-toggle-switch :checked="canAutoDebit" class="pointer-events-none" />
				</div>
			</div>
			<p class="mx-auto mt-4 max-w-[460px] text-center text-sm leading-relaxed text-gray-600">
				{{
					canAutoDebit
						? 'This is a subscription-based plan with auto-renewal enabled. You will be automatically charged on the renewal date.'
						: 'Auto-renewal is disabled. You will need to manually renew this plan before the renewal date.'
				}}
			</p>
		</div>
	</div>

	<dialogs-error
		v-model:show="showTurnOffModal"
		header="Turn Off Auto Renewal"
		:icon-props="{
			dir: 'fill',
			icon: 'alert',
			width: 44,
			height: 44
		}"
		wrapper-class="bg-gray-50 py-7 px-5 w-full rounded-lg"
		:use-cancel-btn="true"
	>
		<template #paragraph>
			<ul class="space-y-4 text-left">
				<li class="flex items-start gap-3">
					<div class="mt-2 h-2 w-2 flex-none rounded-full bg-primary-2" />
					<span class="text-sm text-gray-600 md:text-base">
						Without auto-renewal, you'll need to renew manually to stay covered.
					</span>
				</li>
				<li class="flex items-start gap-3">
					<div class="mt-2 h-2 w-2 flex-none rounded-full bg-primary-2" />
					<span class="text-sm text-gray-600 md:text-base">
						Need help? Contact our support team -
						<a :href="supportLine.link" class="font-semibold text-primary-2"> {{ supportLine.linkText }} </a
						>.
					</span>
				</li>
			</ul>
		</template>

		<template #button>
			<div class="flex w-full items-center justify-between gap-x-4">
				<ui-button variants="two" class="w-full text-gray-700" @click="showTurnOffModal = false">
					Cancel
				</ui-button>
				<ui-button variants="one" class="w-full" @click="contactSupport"> Contact Support </ui-button>
			</div>
		</template>
	</dialogs-error>
</template>
